import { EDesfechoSolicitacao } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import type { TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';

/** O que o analista pode fazer com uma solicitação a partir do card ou da tela de detalhe. */
export type AcaoServico = 'efetivar' | 'cancelar';

/**
 * A ação praticada no portal, na letra que o quadro grava como desfecho da conclusão.
 *
 * Vive aqui — junto de `AcaoServico`, e não dentro de cada componente — porque os DOIS caminhos de
 * conclusão precisam da mesma tradução: o arraste unitário e o encerramento em lote. Duplicada, é
 * onde um dos dois inverte as letras, e o relatório passa a contar cancelamento como efetivação sem
 * nada quebrar.
 */
export function desfechoDaAcao(acao: AcaoServico): EDesfechoSolicitacao {
    return acao === 'efetivar' ? EDesfechoSolicitacao.EFETIVADO : EDesfechoSolicitacao.CANCELADO;
}

/** Campo que a confirmação de um serviço exige além do "deseja realmente?". */
export type CampoConfirmacao = 'nenhum' | 'data' | 'observacao';

/**
 * Código do serviço -> roles de escrita. Vive aqui, e não dentro de um componente, porque a tela de
 * detalhe e o quadro precisam do MESMO mapa: duplicado, é onde um serviço novo entra num lugar e
 * não no outro — e o que vaza é um botão de efetivar para quem não tem a role.
 *
 * Serviço ausente do mapa mantém o default aberto; serviço presente sem a role da ação esconde a
 * ação (fail-closed). Por isso a ausência é `undefined`, e não objeto vazio.
 */
const ROLES_POR_CODIGO: Record<string, { confirmar?: string; cancelar?: string }> = {
    'area-atuacao': { cancelar: PERMISSOES.pessoaFisica.areaAtuacao.cancelar },
    'averbacao-nacionalidade': { confirmar: PERMISSOES.pessoaFisica.averbacaoNacionalidade.confirmar, cancelar: PERMISSOES.pessoaFisica.averbacaoNacionalidade.cancelar },
    'averbacao-nome': { confirmar: PERMISSOES.pessoaFisica.averbacaoNome.confirmar, cancelar: PERMISSOES.pessoaFisica.averbacaoNome.cancelar },
    'cancelamento-falecimento': { confirmar: PERMISSOES.pessoaFisica.cancelamentoFalecimento.confirmar, cancelar: PERMISSOES.pessoaFisica.cancelamentoFalecimento.cancelar },
    'solicitar-2via-carteira': { cancelar: PERMISSOES.pessoaFisica.carteiraProfissional.cancelar },
    'solicitar-2via-cedula-identidade': { cancelar: PERMISSOES.pessoaFisica.cedulaIdentidade.cancelar },
    especialidade: { cancelar: PERMISSOES.pessoaFisica.especialidadeMedica.cancelar },
    'visto-corrido': { confirmar: PERMISSOES.pessoaFisica.vistoCorrido.confirmar, cancelar: PERMISSOES.pessoaFisica.vistoCorrido.cancelar },
    'visto-fracionado': { confirmar: PERMISSOES.pessoaFisica.vistoCorrido.confirmar, cancelar: PERMISSOES.pessoaFisica.vistoCorrido.cancelar },
    'pre-medico': { confirmar: PERMISSOES.pessoaFisica.preMedico.confirmar, cancelar: PERMISSOES.pessoaFisica.preMedico.cancelar },
    'cancelamento-inscricao': { confirmar: PERMISSOES.pessoaFisica.cancelamentoInscricao.confirmar, cancelar: PERMISSOES.pessoaFisica.cancelamentoInscricao.cancelar },
    'consultorio-privado': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    'consultorio-privado-renovacao': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    'consultorio-privado-cancelamento': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    pcmso: { confirmar: PERMISSOES.pessoaFisica.pcmso.confirmar, cancelar: PERMISSOES.pessoaFisica.pcmso.cancelar },
    pos: { confirmar: PERMISSOES.pessoaFisica.posGraduacao.confirmar, cancelar: PERMISSOES.pessoaFisica.posGraduacao.cancelar },
    'regimento-interno': { confirmar: PERMISSOES.pessoaJuridica.regimentoInterno.confirmar, cancelar: PERMISSOES.pessoaJuridica.regimentoInterno.cancelar },
    'comissao-etica': { confirmar: PERMISSOES.pessoaJuridica.comissaoEtica.confirmar, cancelar: PERMISSOES.pessoaJuridica.comissaoEtica.cancelar },
    'cancelamento-pj': { confirmar: PERMISSOES.pessoaJuridica.cancelamentoPj.confirmar, cancelar: PERMISSOES.pessoaJuridica.cancelamentoPj.cancelar },
    'visto-alteracao-contratual': { confirmar: PERMISSOES.pessoaJuridica.alteracaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.alteracaoCadastral.cancelar },
    'alteracao-cadastral': { confirmar: PERMISSOES.pessoaJuridica.alteracaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.alteracaoCadastral.cancelar },
    'alteracao-contratual': { confirmar: PERMISSOES.pessoaJuridica.alteracaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.alteracaoCadastral.cancelar },
    'diretor-clinico': { confirmar: PERMISSOES.pessoaJuridica.diretorClinico.confirmar, cancelar: PERMISSOES.pessoaJuridica.diretorClinico.cancelar },
    'diretor-tecnico-solicitacao-pj': { confirmar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.confirmar, cancelar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.cancelar },
    'pre-prestador': { confirmar: PERMISSOES.pessoaJuridica.prePrestador.confirmar, cancelar: PERMISSOES.pessoaJuridica.prePrestador.cancelar },
    'renovacao-cadastral': { confirmar: PERMISSOES.pessoaJuridica.renovacaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.renovacaoCadastral.cancelar },
    'diretor-tecnico-solicitacao-pf': { confirmar: PERMISSOES.pessoaJuridica.solicitacaoDiretorTecnico.confirmar, cancelar: PERMISSOES.pessoaJuridica.solicitacaoDiretorTecnico.cancelar },
    'cart-evento': { confirmar: PERMISSOES.pessoaJuridica.cartEvento.confirmar, cancelar: PERMISSOES.pessoaJuridica.cartEvento.cancelar },
    // Aliases de codigo (registry) -> mesma role do canonico, para nao vazar em deep-link/navegacao nao-canonica.
    'cancelar-por-falecimento': { confirmar: PERMISSOES.pessoaFisica.cancelamentoFalecimento.confirmar, cancelar: PERMISSOES.pessoaFisica.cancelamentoFalecimento.cancelar },
    'cancelamento-de-inscricao': { confirmar: PERMISSOES.pessoaFisica.cancelamentoInscricao.confirmar, cancelar: PERMISSOES.pessoaFisica.cancelamentoInscricao.cancelar },
    'responsavel-pcmso': { confirmar: PERMISSOES.pessoaFisica.pcmso.confirmar, cancelar: PERMISSOES.pessoaFisica.pcmso.cancelar },
    'cadastro-consultorio-privado': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    'renovacao-consultorio-privado': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    'cancelamento-consultorio-privado': { confirmar: PERMISSOES.pessoaFisica.consultorioPrivado.confirmar, cancelar: PERMISSOES.pessoaFisica.consultorioPrivado.cancelar },
    'pos-graduacao': { confirmar: PERMISSOES.pessoaFisica.posGraduacao.confirmar, cancelar: PERMISSOES.pessoaFisica.posGraduacao.cancelar },
    'area-de-atuacao': { cancelar: PERMISSOES.pessoaFisica.areaAtuacao.cancelar },
    'carteira-profissional': { cancelar: PERMISSOES.pessoaFisica.carteiraProfissional.cancelar },
    'cedula-de-identidade': { cancelar: PERMISSOES.pessoaFisica.cedulaIdentidade.cancelar },
    especialidades: { cancelar: PERMISSOES.pessoaFisica.especialidadeMedica.cancelar },
    'diretoria-clinica': { confirmar: PERMISSOES.pessoaJuridica.diretorClinico.confirmar, cancelar: PERMISSOES.pessoaJuridica.diretorClinico.cancelar },
    // Aliases PJ -> mesma role do canonico.
    'regimento-interno-corpo-clinico': { confirmar: PERMISSOES.pessoaJuridica.regimentoInterno.confirmar, cancelar: PERMISSOES.pessoaJuridica.regimentoInterno.cancelar },
    'solicitacao-diretor': { confirmar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.confirmar, cancelar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.cancelar },
    'solicitacao-diretor-tecnico': { confirmar: PERMISSOES.pessoaJuridica.solicitacaoDiretorTecnico.confirmar, cancelar: PERMISSOES.pessoaJuridica.solicitacaoDiretorTecnico.cancelar },
    renovacao: { confirmar: PERMISSOES.pessoaJuridica.renovacaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.renovacaoCadastral.cancelar },
    'renovacao-servicos': { confirmar: PERMISSOES.pessoaJuridica.renovacaoCadastral.confirmar, cancelar: PERMISSOES.pessoaJuridica.renovacaoCadastral.cancelar },
    'saida-diretor-tecnico': { confirmar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.confirmar, cancelar: PERMISSOES.pessoaJuridica.solicitacaoDiretor.cancelar }
};

/**
 * Serviços cuja efetivação tem passo próprio que o `finalizarSolicitacao` genérico do facade NÃO
 * reproduz. Neles o card não oferece efetivar: o analista usa a tela de detalhe, que é quem sabe
 * fazer o passo. Efetivar pelo quadro mandaria ao portal um corpo diferente do que a tela manda —
 * e sem erro nenhum, que é a pior forma de errar. Cancelar continua valendo pelo card em todos.
 *
 * A lista é derivada do código, um a um: para cada código, o `confirmarSolicitacao*` correspondente
 * no `dashboard-servicos.facade` foi comparado ao caminho genérico. Serviço cuja única diferença o
 * `completarDtoQuandoOServicoExige` já reproduz (injeção de `codigoServico`, data ou observação)
 * fica de fora daqui de propósito — consultório privado e alteração PJ são esse caso.
 */
const EFETIVACAO_SO_NA_TELA_DE_DETALHE = new Set([
    // Wizard de etapas com validação própria: a efetivação só sai do `concluirWizard()`, que ainda
    // recusa enquanto houver alteração não salva.
    'pre-medico',
    // 'pre-prestador' SAIU daqui: o motivo original (window.prompt do CRM) já não existe e a
    // efetivação usa o caminho genérico, então não há corpo divergente a temer. A saída foi decidida
    // junto com a inscrição de ofício, que precisa acontecer pelos dois gestos — botão e arraste —
    // para nenhum deles virar atalho para pular a regra. O que substitui a proteção aqui é a trava de
    // ORIGEM no diálogo de conclusão (`podeEfetivarAoConcluir`): não se conclui do Backlog.
    // O `consultar` recebe um 4º parâmetro (`codigoServico`) que o contrato genérico não tem.
    'visto-corrido',
    'visto-fracionado',
    // Sem data informada, usa a data de HOJE (data do ato administrativo). O genérico não inventa data.
    'cancelamento-inscricao',
    'cancelamento-de-inscricao',
    // A data cai para `dto.dtCancelamento` e, faltando ela, é derivada de `dto.dataHora`.
    'cancelamento-pj',
    // `window.confirm` quando o diretor atual é sócio e, nesse ramo, um efetivar diferente
    // (`aprovado: 'S'`). O alias `saida-diretor-tecnico` aponta para o mesmo service.
    'solicitacao-diretor',
    'diretor-tecnico-solicitacao-pj',
    'saida-diretor-tecnico'
]);

/**
 * Serviços do Set acima que MESMO ASSIM entram no encerramento em lote. Lista **opt-in** de
 * propósito, e nunca opt-out: assim, um serviço novo que amanhã entre em
 * `EFETIVACAO_SO_NA_TELA_DE_DETALHE` fica de fora do lote por omissão — o lado seguro. Um opt-out o
 * colocaria dentro sem ninguém decidir.
 *
 * A exclusão do Set acima tem duas razões DIFERENTES, e só uma delas o lote pode dispensar:
 *
 * - **Conferência de tela** — o serviço efetiva pelo caminho genérico, e o que a tela de detalhe
 *   acrescenta é revisão humana protocolo a protocolo. É exatamente disso que o analista abre mão,
 *   conscientemente, ao escolher o lote. São os dois daqui:
 *   - `pre-medico`: o wizard valida etapa a etapa antes de concluir; a efetivação em si é a genérica.
 *   - `pre-prestador`: o motivo original (o `window.prompt` do CRM) já foi removido do código, e a
 *     efetivação já usa o caminho genérico — o comentário no Set diz que sair dali é decisão desta
 *     spec.
 *
 * - **Diferença técnica de payload** — o caminho genérico NÃO reproduz o que aquele serviço precisa,
 *   e pular o gate não custaria uma conferência: gravaria o ato errado, em silêncio. É o caso dos
 *   outros sete, e por isso nenhum deles está aqui:
 *   - `visto-corrido`/`visto-fracionado`: o `consultar` recebe um 4º parâmetro (`codigoServico`) que
 *     o contrato genérico não tem;
 *   - `cancelamento-inscricao`/`cancelamento-de-inscricao`: sem data informada usam a data de HOJE, e
 *     o genérico não inventa data;
 *   - `cancelamento-pj`: a data cai para `dto.dtCancelamento` e, faltando, deriva de `dto.dataHora`;
 *   - `solicitacao-diretor`/`diretor-tecnico-solicitacao-pj`/`saida-diretor-tecnico`: quando o
 *     diretor atual é sócio, o ramo usa um **efetivar diferente** (`aprovado: 'S'`) — não é uma
 *     conferência a menos, é outro ato administrativo.
 *
 * Nada é removido de `EFETIVACAO_SO_NA_TELA_DE_DETALHE`: ele continua governando a tela unitária,
 * onde a exclusão é legítima para os nove.
 */
const EFETIVACAO_HABILITADA_NO_LOTE = new Set(['pre-medico', 'pre-prestador']);

/**
 * O gate de tela, relido para o encerramento em lote. Só quem está na lista opt-in acima escapa da
 * exclusão; para todo o resto a resposta é a mesma da tela unitária.
 */
export function efetivacaoSoNaTelaDeDetalheNoLote(codigo: string): boolean {
    return efetivacaoSoNaTelaDeDetalhe(codigo) && !EFETIVACAO_HABILITADA_NO_LOTE.has(codigo);
}

const CONFIRMACAO_COM_DATA_DE_CANCELAMENTO = new Set(['cancelamento-falecimento', 'cancelar-por-falecimento', 'cancelamento-inscricao', 'cancelamento-de-inscricao', 'cancelamento-pj']);

const CONFIRMACAO_COM_DATA_DE_HOMOLOGACAO = new Set(['regimento-interno', 'regimento-interno-corpo-clinico']);

const CONFIRMACAO_COM_DATA_DA_DIRETORIA = new Set(['solicitacao-diretor', 'diretor-tecnico-solicitacao-pj', 'solicitacao-diretor-tecnico', 'diretor-tecnico-solicitacao-pf', 'diretor-clinico', 'diretoria-clinica']);

const CONFIRMACAO_COM_OBSERVACAO = new Set(['alteracao-cadastral', 'alteracao-contratual', 'visto-alteracao-contratual']);

export function rolesDoServico(codigo: string): { confirmar?: string; cancelar?: string } | undefined {
    return ROLES_POR_CODIGO[codigo];
}

/** Cart evento só existe no RJ: fora dele, nem efetivar nem cancelar fazem sentido. */
export function acoesOcultasPorUf(codigo: string, uf?: string): boolean {
    return codigo === 'cart-evento' && (uf ?? '').trim().toUpperCase() !== 'RJ';
}

export function campoDaConfirmacao(codigo: string): CampoConfirmacao {
    if (CONFIRMACAO_COM_DATA_DE_CANCELAMENTO.has(codigo) || CONFIRMACAO_COM_DATA_DE_HOMOLOGACAO.has(codigo) || CONFIRMACAO_COM_DATA_DA_DIRETORIA.has(codigo)) {
        return 'data';
    }
    return CONFIRMACAO_COM_OBSERVACAO.has(codigo) ? 'observacao' : 'nenhum';
}

/** Rótulo do campo, que muda de nome conforme o serviço mesmo quando o tipo é o mesmo. */
export function rotuloDoCampoDeConfirmacao(codigo: string): string {
    if (CONFIRMACAO_COM_DATA_DE_CANCELAMENTO.has(codigo)) {
        return 'Data do cancelamento';
    }
    if (CONFIRMACAO_COM_DATA_DE_HOMOLOGACAO.has(codigo)) {
        return 'Data da homologação';
    }
    if (CONFIRMACAO_COM_DATA_DA_DIRETORIA.has(codigo)) {
        return 'Data da diretoria';
    }
    return CONFIRMACAO_COM_OBSERVACAO.has(codigo) ? 'Observação' : '';
}

export function efetivacaoSoNaTelaDeDetalhe(codigo: string): boolean {
    return EFETIVACAO_SO_NA_TELA_DE_DETALHE.has(codigo);
}

/**
 * Serviço que identifica o solicitante pelo CNPJ da empresa, e não pelo CRM. Hoje só o
 * pré-prestador: ali a empresa ainda não foi cadastrada, não há CRM nenhum, e o que a identifica é
 * o CNPJ.
 */
export function identificacaoPorCnpj(codigo: string): boolean {
    return codigo === 'pre-prestador';
}

/**
 * O rosto do card é de OUTRA pessoa, não de quem está escrito ao lado do nome: em todo protocolo de
 * pessoa jurídica o sujeito é a empresa, mas quem está sob análise é o médico diretor técnico
 * responsável por ela — é o rosto dele que o card mostra.
 *
 * A regra era por serviço (só o pré-prestador). Passou a ser pela NATUREZA do solicitante porque não
 * é uma particularidade daquele serviço: em qualquer protocolo de PJ a empresa não tem rosto, e o
 * único rosto disponível é o do diretor.
 *
 * Uma única checagem para os dois usos que dependem dela: `crmUfDoRostoDoCard` (qual CRM buscar) e
 * o rótulo do avatar na tela (decorativo ou anunciado). Resolvidas em separado, uma poderia decidir
 * "diretor técnico" e a outra "titular do protocolo" para o mesmo card.
 */
export function rostoDoCardEhDoDiretorTecnico(tipo: TipoServicoPessoa): boolean {
    return tipo === 'pessoa-juridica';
}

/**
 * CRM/UF que alimenta o rosto do card — usado tanto para buscar a foto (biometria) quanto para a
 * chave de deduplicação do cache de fotos. Resolvido aqui, num único lugar: se a busca e a chave
 * resolvessem o CRM cada uma por conta própria, um card poderia buscar a foto de uma pessoa e
 * guardá-la sob a chave de outra.
 *
 * Na pessoa jurídica vem do diretor técnico (`diretorTecnicoCrmUf`); na pessoa física, do próprio
 * solicitante (`crmUf`).
 */
export function crmUfDoRostoDoCard(tipo: TipoServicoPessoa, item: { crmUf?: string; diretorTecnicoCrmUf?: string }): string | undefined {
    return rostoDoCardEhDoDiretorTecnico(tipo) ? item.diretorTecnicoCrmUf : item.crmUf;
}

/**
 * Data do `input[type=date]` (ISO) no formato que o portal espera no payload. Vive aqui porque é o
 * contrato do envio, e não a preferência de uma tela: card e tela de detalhe precisam mandar igual.
 *
 * Data incompleta devolve `undefined` em vez de `//` — string mal formada chegaria ao portal
 * parecendo data informada.
 */
export function dataDeConfirmacaoParaPayload(dataIso: string): string | undefined {
    const [ano, mes, dia] = (dataIso ?? '').trim().split('-');
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : undefined;
}

/** Qual chave do payload recebe a data, que difere por serviço mesmo com o mesmo campo na tela. */
export function chaveDaDataDeConfirmacao(codigo: string): 'dtCancelamento' | 'dtHomologacao' | 'dataDiretoria' | null {
    if (CONFIRMACAO_COM_DATA_DE_CANCELAMENTO.has(codigo)) {
        return 'dtCancelamento';
    }
    if (CONFIRMACAO_COM_DATA_DE_HOMOLOGACAO.has(codigo)) {
        return 'dtHomologacao';
    }
    return CONFIRMACAO_COM_DATA_DA_DIRETORIA.has(codigo) ? 'dataDiretoria' : null;
}
